// App.js - Updated with proper route handling
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from './components/sidebar/sidebar';
import Login from './pages/Auth/login';
import Dashboard from './pages/Dashboard/dashboard';
import Clients from "./pages/Clients/clients";
import Dealers from "./pages/Dealers/dealers";
import Offices from './pages/Offices/offices';
import Technicians from './pages/Technicians/technician';
import Devices from './pages/Devices/devices';
import DeviceDashboard from './pages/Devices/DeviceDashboard';
import ProductionDevices from './pages/ProductionDevices/productiondevices';
import OLEDDevices from './pages/Oleddevices/oleddevices';
import ClientOfficer from './pages/ClientOfficer/clientofficer';
import Removals from './pages/Removals/removals';
import Firmwares from './pages/Firmwares/firmwares';
import Management from './pages/Management/management';
import CalibrationKit from './pages/CalibrationKit/calibrationkit';
import LockoutStatus from './pages/LockoutStatus/lockoutstatus';
import Geofence from './pages/Geofence/geofence';
import Reports from './pages/Reports/reports';
import Payment from "./pages/Payments/payment";
import Appointments from './pages/Appointments/appointments';
import Announcement from './pages/Announcement/announcement';
import './App.css';
import { Menu } from 'lucide-react';

// ✅ FIXED: Simple and reliable auth check
const isUserLoggedIn = () => {
  if (sessionStorage.getItem('justLoggedOut') === 'true') {
    return false;
  }

  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const hasUser = !!(
    localStorage.getItem('admin_user') ||
    localStorage.getItem('fleetuser') ||
    localStorage.getItem('alcotrackerparentuser') ||
    localStorage.getItem('aargeeuser')
  );
  return isLoggedIn && hasUser;
};

// ✅ FIXED: ProtectedRoute with loop prevention
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const hasAccess = isAuthenticated && isUserLoggedIn();

  if (hasAccess) {
    return children;
  }

  if (location.pathname === '/login') {
    return null;
  }

  return <Navigate to="/login" replace />;
};

const getAuthenticatedLandingPath = () => {
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    const email = String(user.emailId || user.email || '').trim().toLowerCase();
    return email === 'alcobrakeproduction@gmail.com' || String(user.role || user.accesslevel || '').toLowerCase() === 'office'
      ? '/clients'
      : '/dashboard';
  } catch (error) {
    return '/dashboard';
  }
};

const RootRedirect = () => {
  const location = useLocation();
  const targetPath = isUserLoggedIn() ? getAuthenticatedLandingPath() : "/login";

  if (location.pathname === targetPath) {
    return null;
  }

  return <Navigate to={targetPath} replace />;
};

const LoginRoute = () => {
  const location = useLocation();
  const targetPath = getAuthenticatedLandingPath();

  if (isUserLoggedIn() && location.pathname !== targetPath) {
    return <Navigate to={targetPath} replace />;
  }

  return <Login />;
};

// Create a wrapper component that forces re-render on route change
const RouteChangeWrapper = ({ children, location }) => {
  return (
    <div key={location.pathname}>
      {children}
    </div>
  );
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });

  // Theme effect
  useEffect(() => {
    if (isDarkMode) {
      document.body.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  const handleNavigate = (path) => navigate(path);

  return (
    <div className="app">
      <Sidebar
        onNavigate={handleNavigate}
        currentPath={location.pathname}
        isMobileOpen={isMobileOpen}
        onClose={() => setIsMobileOpen(false)}
        toggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
      />
      
      <div className="app-main-wrapper">
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileOpen(true)}
          aria-label="Toggle Sidebar"
        >
          <Menu size={24} />
        </button>
        <main className="app-content">
          <RouteChangeWrapper location={location}>
            {children}
          </RouteChangeWrapper>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route
          path="/login"
          element={<LoginRoute />}
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="dealers" element={<Dealers />} />
                  <Route path="offices" element={<Offices />} />
                  <Route path="technician" element={<Technicians />} />
                  <Route path="devices" element={<Devices />} />
                  <Route path="production-dashboard" element={<DeviceDashboard />} />
                  <Route path="production-devices" element={<ProductionDevices />} />
                  <Route path="oleddevices" element={<OLEDDevices />} />
                  <Route path="clientofficer" element={<ClientOfficer />} />
                  <Route path="removals" element={<Removals />} />
                  <Route path="firmwares" element={<Firmwares />} />
                  <Route path="management" element={<Management />} />
                  <Route path="calibrationkit" element={<CalibrationKit />} />
                  <Route path="lockoutstatus" element={<LockoutStatus />} />
                  <Route path="geo-fencing" element={<Geofence />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="payments" element={<Payment />} />
                  <Route path="autopay" element={<Payment />} />
                  <Route path="appointments" element={<Appointments />} />
                  <Route path="announcement" element={<Announcement />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
